// src/tattoo/components/ArtistDirectoryBrowser.jsx
import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import { supabase, createLead } from "../lib/supabaseClient";
import { Link } from "react-router-dom";
import { getOrCreateShareUrlForCurrentProject } from "../share";
import { tattoo } from "../state";
import {
  formatLocation,
  getStyleArray,
  formatStyles,
  getInitials,
  getWebsiteUrl,
  getInstagramUrl,
  buildMapsUrl,
} from "../lib/shopUtils";

// ---------- ArtistDirectory component ----------
export default function ArtistDirectoryBrowser() {
  const [shops, setShops] = useState([]);
  const [shopsLoading, setShopsLoading] = useState(true);
  const [shopsError, setShopsError] = useState(null);
  const [selectedShopId, setSelectedShopId] = useState(null);

  function onSelectShop(id) {
    setSelectedShopId((prev) => (prev === id ? null : id));
  }

  useEffect(() => {
    let cancelled = false;
  
    async function loadShops() {
      try {
        setShopsLoading(true);
        setShopsError(null);
  
        const { data, error } = await supabase
          .from("shops")
          .select("*")
          .eq("is_active", true)
          .order("name", { ascending: true });
  
        if (error) throw error;
        if (cancelled) return;
  
        setShops(data || []);
      } catch (err) {
        console.error("[artist browser] failed to load shops", err);
        if (!cancelled) {
          setShopsError("Couldn’t load artists right now.");
        }
      } finally {
        if (!cancelled) setShopsLoading(false);
      }
    }
  
    loadShops();
  
    return () => {
      cancelled = true;
    };
  }, []);

  const [filter, setFilter] = useState("");
  const [styleFilter, setStyleFilter] = useState(null);
  const [userLoc, setUserLoc] = useState(null);
  const [geoAsked, setGeoAsked] = useState(false);
  const [sortMode, setSortMode] = useState("default"); // "default" | "nearest"
  const [favorites, setFavorites] = useState(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem("tt_fav_shops") || "[]";
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  const listRef = useRef(null);

  // ---------- tiny helpers (only used inside this component) ----------

  const toggleFavorite = useCallback((shopId) => {
    setFavorites((prev) =>
      prev.includes(shopId)
        ? prev.filter((id) => id !== shopId)
        : [...prev, shopId]
    );
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem("tt_fav_shops", JSON.stringify(favorites));
    } catch {
      // ignore
    }
  }, [favorites]);

  function getShopCoords(shop) {
    const lat = shop.latitude ?? shop.lat ?? shop.lat_deg ?? null;
    const lng = shop.longitude ?? shop.lng ?? shop.lon ?? shop.long_deg ?? null;
    if (typeof lat === "number" && typeof lng === "number") {
      return { lat, lng };
    }
    return null;
  }

  function getDistanceKm(userLoc, shop) {
    if (!userLoc) return null;
    const coords = getShopCoords(shop);
    if (!coords) return null;

    const R = 6371; // km
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(coords.lat - userLoc.lat);
    const dLon = toRad(coords.lng - userLoc.lng);
    const lat1 = toRad(userLoc.lat);
    const lat2 = toRad(coords.lat);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  function formatDistance(km) {
    if (km == null || !isFinite(km)) return "";
    if (km < 1) return `${Math.round(km * 1000)} m`;
    if (km < 100) return `${Math.round(km)} km`;
    return `${Math.round(km)} km`;
  }

  // ---------- geolocation (once) ----------

  useEffect(() => {
    if (geoAsked) return;
    setGeoAsked(true);

    if (typeof navigator === "undefined" || !navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLoc({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
      },
      () => {
        // user denied / unavailable – ignore
      },
      { enableHighAccuracy: false, timeout: 5000 }
    );
  }, [geoAsked]);

  // ---------- collect style pills ----------

  const allStyles = useMemo(() => {
    const set = new Set();
    for (const shop of shops || []) {
      getStyleArray(shop).forEach((s) => set.add(s));
    }
    return Array.from(set).sort((a, b) =>
      a.toLowerCase().localeCompare(b.toLowerCase())
    );
  }, [shops]);

  // ---------- filter + sort (default / nearest, favorites first) ----------

  const filteredShops = useMemo(() => {
    const term = filter.trim().toLowerCase();
    const hasTerm = term.length > 0;
    const hasStyleFilter = !!styleFilter;
    const favSet = new Set(favorites);

    let result = (shops || []).filter((shop) => {
      if (hasStyleFilter) {
        const styles = getStyleArray(shop).map((s) => s.toLowerCase());
        if (!styles.includes(styleFilter.toLowerCase())) return false;
      }

      if (!hasTerm) return true;

      const loc = formatLocation(shop);
      const styles = formatStyles(shop);
      const text = [shop.name, shop.display_name, shop.tagline, loc, styles]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return text.includes(term);
    });

    // Favorites first, then by mode
    result = result.slice().sort((a, b) => {
      const favA = favSet.has(a.id);
      const favB = favSet.has(b.id);
      if (favA !== favB) return favA ? -1 : 1;

      if (sortMode === "nearest" && userLoc) {
        const da = getDistanceKm(userLoc, a) ?? Number.POSITIVE_INFINITY;
        const db = getDistanceKm(userLoc, b) ?? Number.POSITIVE_INFINITY;
        if (isFinite(da) || isFinite(db)) {
          return da - db;
        }
      }

      // fallback: alpha by name
      const na = (a.display_name || a.name || "").toLowerCase();
      const nb = (b.display_name || b.name || "").toLowerCase();
      return na.localeCompare(nb);
    });

    return result;
  }, [shops, filter, styleFilter, sortMode, favorites, userLoc]);

  const selectedShop = (shops || []).find((s) => s.id === selectedShopId);

  // Smoothly scroll selected card into view
  useEffect(() => {
    if (!selectedShopId || !listRef.current) return;
    const el = listRef.current.querySelector(
      `[data-artist-id="${selectedShopId}"]`
    );
    if (el) {
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [selectedShopId]);

  const canUseNearest = !!userLoc;

  return (
    <div className="tt-lead-card" id="artist-directory">
      <h3 className="tt-lead-title">Artist directory</h3>

<p className="tt-lead-subtitle">
  Browse artists, view profiles, and find flash.
</p>

      {/* text filter */}
      <input
        className="tt-lead-input"
        placeholder="Filter by studio, city, or style…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
      />

      {/* quick style filter pills */}
      {allStyles.length > 0 && (
        <div className="tt-style-filter-row">
          {allStyles.slice(0, 8).map((style) => {
            const on = styleFilter === style;

            const baseStyle = {
              borderRadius: 999,
              border: "1px solid #333",
              background: "#181818",
              color: "#f5f5f5",
              padding: "4px 10px",
              fontSize: 11,
              cursor: "pointer",
              textTransform: "capitalize",
              transition:
                "background 0.15s ease, border-color 0.15s ease, color 0.15s ease",
            };

            const selectedStyle = {
              borderColor: "#a2332b",
              background: "#a2332b",
              color: "#ffffff",
            };

            return (
              <button
                key={style}
                type="button"
                className={`tt-style-pill ${on ? "is-on" : ""}`}
                style={on ? { ...baseStyle, ...selectedStyle } : baseStyle}
                onClick={() =>
                  setStyleFilter((prev) => (prev === style ? null : style))
                }
              >
                {style}
                {on ? "✓ " : ""}
              </button>
            );
          })}
        </div>
      )}

      {/* sort mode row */}
      <div
        style={{
          marginTop: 8,
          display: "flex",
          justifyContent: "flex-end",
          gap: 6,
          fontSize: 11,
        }}
      >
        <span style={{ opacity: 0.6, alignSelf: "center" }}>Sort:</span>
        <button
          type="button"
          className={`tt-sort-pill ${sortMode === "default" ? "is-on" : ""}`}
          onClick={() => setSortMode("default")}
        >
          Default
        </button>
        <button
          type="button"
          className={`tt-sort-pill ${sortMode === "nearest" ? "is-on" : ""}`}
          onClick={() => canUseNearest && setSortMode("nearest")}
          disabled={!canUseNearest}
          style={
            !canUseNearest ? { opacity: 0.4, cursor: "not-allowed" } : undefined
          }
        >
          Nearest
        </button>
      </div>

      {shopsLoading && <div className="tt-lead-status">Loading artists…</div>}

      {shopsError && !shopsLoading && (
        <div className="tt-lead-error">Couldn’t load artists right now.</div>
      )}

      {!shopsLoading && filteredShops.length > 0 && (
        <div
          ref={listRef}
          className="tt-artist-list"
          style={{
            marginTop: 12,
            display: "flex",
            flexDirection: "column",
            gap: 10,
            maxHeight: 280,
            overflowY: "auto",
            overflowX: "hidden",
            paddingRight: 4,
            boxSizing: "border-box",
          }}
        >
          {filteredShops.map((shop) => {
            const isSelected = selectedShopId === shop.id;
            const displayName = shop.display_name || shop.name;
            const loc = formatLocation(shop);
            const styles = formatStyles(shop); // ← keep for filtering, but won’t render inline
            const styleArr = getStyleArray(shop);
            const mapsUrl = buildMapsUrl(shop);
            const websiteUrl = getWebsiteUrl(shop);
            const instagramUrl = getInstagramUrl(shop);
            const hasCoords =
              userLoc &&
              typeof shop.lat === "number" &&
              typeof shop.lng === "number";

            let distanceLabel = "";
            if (hasCoords) {
              const R = 6371; // km
              const dLat = ((shop.lat - userLoc.lat) * Math.PI) / 180;
              const dLng = ((shop.lng - userLoc.lng) * Math.PI) / 180;
              const a =
                Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                Math.cos((userLoc.lat * Math.PI) / 180) *
                  Math.cos((shop.lat * Math.PI) / 180) *
                  Math.sin(dLng / 2) *
                  Math.sin(dLng / 2);
              const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
              const km = R * c;

              if (km < 1) {
                const m = Math.round(km * 1000);
                distanceLabel = `${m} m away`;
              } else if (km < 10) {
                distanceLabel = `${km.toFixed(1)} km away`;
              } else {
                distanceLabel = `${Math.round(km)} km away`;
              }
            }
            const distanceKm = getDistanceKm(userLoc, shop);
            const isFav = favorites.includes(shop.id);
            const avatarUrl = shop.avatar_url || shop.logo_url || null;

            return (
              <button
                key={shop.id}
                type="button"
                data-artist-id={shop.id}
                onClick={() => onSelectShop(isSelected ? null : shop.id)}
                className={`tt-artist-chip ${isSelected ? "is-selected" : ""}`}
                style={{
                  width: "100%",
                  maxWidth: "100%",
                  boxSizing: "border-box",
                  textAlign: "left",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 12,
                  padding: "12px 14px",
                  borderRadius: 18,
                  border: isSelected
                    ? "1px solid #ffffff"
                    : "1px solid #2a2a2a",
                  background: isSelected ? "#f5f5f5" : "#141414",
                  color: isSelected ? "#111" : "#f5f5f5",
                  overflow: "hidden",
                  flexShrink: 0,
                  transition: "background 0.16s ease, border-color 0.16s ease",
                }}
              >
                {/* avatar — studio icon/logo when the artist has uploaded
                    one (shops.avatar_url, set via the Studio Images page),
                    falling back to initials-on-color otherwise. */}
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: "999px",
                    background: "#a2332b",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 600,
                    fontSize: 16,
                    flexShrink: 0,
                    color: "#fff",
                    overflow: "hidden",
                  }}
                >
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt=""
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    getInitials(displayName)
                  )}
                </div>

                {/* main text + extras */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {/* name + favorite star */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: 600,
                        whiteSpace: "normal",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        flex: 1,
                      }}
                    >
                      {displayName}
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(shop.id);
                      }}
                      style={{
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        fontSize: 16,
                        lineHeight: 1,
                        color: isFav
                          ? isSelected
                            ? "#c28a00"
                            : "#ffd35a"
                          : isSelected
                          ? "#555"
                          : "#888",
                      }}
                      aria-label={
                        isFav ? "Remove from favorites" : "Add to favorites"
                      }
                    >
                      {isFav ? "★" : "☆"}
                    </button>
                  </div>

                  {/* tagline */}
                  {shop.tagline && (
                    <div
                      style={{
                        fontSize: 14,
                        opacity: isSelected ? 0.95 : 0.8,
                        whiteSpace: isSelected ? "normal" : "nowrap",
                        overflow: isSelected ? "visible" : "hidden",
                        textOverflow: isSelected ? "clip" : "ellipsis",
                      }}
                    >
                      {shop.tagline}
                    </div>
                  )}

                  {/* location ONLY (no styles here) */}
                  {loc && (
                    <div
                      style={{
                        fontSize: 13,
                        opacity: isSelected ? 0.9 : 0.7,
                        whiteSpace: isSelected ? "normal" : "nowrap",
                        overflow: isSelected ? "visible" : "hidden",
                        textOverflow: isSelected ? "clip" : "ellipsis",
                      }}
                    >
                      {loc}
                    </div>
                  )}
                  {/* distance line – separate + softer */}
                  {distanceLabel && (
                    <div
                      style={{
                        marginTop: 2,
                        fontSize: 12,
                        opacity: isSelected ? 0.75 : 0.6,
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <span style={{ fontSize: 11 }}>📍</span>
                      <span>{distanceLabel}</span>
                    </div>
                  )}

                  {/* expanded extras */}
                  {isSelected && (
                    <div className="tt-artist-extra">
                      {styleArr.length > 0 && (
                        <div className="tt-artist-tags">
                          {styleArr.slice(0, 6).map((style) => (
                            <span key={style} className="tt-artist-tag">
                              {style}
                            </span>
                          ))}
                        </div>
                      )}

                      {(mapsUrl || websiteUrl || instagramUrl || shop.slug) && (
                        <div className="tt-artist-links">
                          {mapsUrl && (
                            <a
                              href={mapsUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="tt-artist-link"
                            >
                              📍 Map
                            </a>
                          )}

                          {websiteUrl && (
                            <a
                              href={websiteUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="tt-artist-link"
                            >
                              🌐 Web
                            </a>
                          )}

                          {instagramUrl && (
                            <a
                              href={instagramUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="tt-artist-link"
                            >
                              📸 IG
                            </a>
                          )}

                          {shop.slug && (
                            <Link
                              to={`/artists/${shop.slug}`}
                              className="tt-artist-link"
                              onClick={(e) => e.stopPropagation()}
                            >
                              🧾 Profile
                            </Link>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

    
    </div>
  );
}

